import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import useLockBodyScroll from 'react-use/lib/useLockBodyScroll';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { Helmet } from 'react-helmet';
import parser from 'html-react-parser';
import { Button, Reveal, Tooltip } from 'chaoskit/src/components';
import { config as ckConfig } from 'chaoskit/src/helpers/config';
import { isExternalUrl } from 'chaoskit/src/helpers/utility';
import { styles } from 'chaoskit/src/helpers/styles';

import Foundation from './Foundation';

import { Icon, Link } from '../components';

import logoDocs from '../assets/media/logo-docs.svg';
import logoMascot from '../assets/media/logo-mascot.svg';
import DocSearch from '../partials/docs/DocSearch';

const DocsLayout = ({ article, nodeType, children, location }) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const {
    config,
    sections: { edges: sections },
    articles: { edges: articles },
    navigation: {
      childDataYaml: { navigation },
    },
  } = useStaticQuery(graphql`
    query DocsLayoutPageData {
      config: site {
        siteMetadata {
          title
          description
        }
      }

      sections: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/src/docs/sections/" }
          frontmatter: {
            title: { ne: "BLUEPRINT" }
            title: { ne: "Overview" } # Hide Overview section from navigation
          }
        }
      ) {
        edges {
          node {
            fileAbsolutePath
            frontmatter {
              title
              icon
            }
          }
        }
      }

      articles: allMarkdownRemark(
        sort: { fields: [frontmatter___title], order: ASC }
        filter: {
          fileAbsolutePath: { regex: "/src/docs/articles/" }
          frontmatter: { title: { ne: "BLUEPRINT" } }
        }
      ) {
        edges {
          node {
            ...DocsArticlesSingleFragment
          }
        }
      }

      navigation: file(name: { eq: "docs__navigation" }) {
        childDataYaml {
          navigation {
            section
            items {
              title
              url
            }
          }
        }
      }
    }
  `);

  useUpdateEffect(() => {
    const $html = document.documentElement;

    if (isMobileNavOpen) {
      $html.classList.add('has-mobileNav');

      // We're not using a sticky header here, so this takes care of sticking header to the top of the viewport if the user scrolls down "juuuust a bit" and clicks the mobile toggle
      window.scrollTo(0, 0);
    } else {
      $html.classList.remove('has-mobileNav');
    }
  }, [isMobileNavOpen]);

  // Make sure we close mobile nav if resizing from mobile to desktop
  const checkSize = event => {
    if (event.matches) {
      setIsMobileNavOpen(false);
    }
  };

  useEffect(() => {
    const viewportMediumMin = window.matchMedia(
      `(min-width: ${styles.viewport.medium})`
    );

    viewportMediumMin.addListener(checkSize);

    window.onpopstate = event => {
      if (event.srcElement.location.pathname === event.target.location.pathname)
        return;

      setIsMobileNavOpen(false);
    };

    return () => {
      viewportMediumMin.removeListener(checkSize);
    };
  }, []);

  // Make sure we kill mobile nav styles between routes
  const handleNavClick = () => {
    if (isMobileNavOpen) {
      setIsMobileNavOpen(false);
    }
  };

  const getMenuItems = () => {
    // Start "cleaner" menu array that we can map through later
    const menu = [];

    // Find relevant sections
    navigation.forEach((pocket, index) => {
      const matchedSection = sections.find(
        ({ node }) => node.frontmatter.title === pocket.section
      );

      if (matchedSection) {
        menu.push({
          ...matchedSection.node.frontmatter,
          fileAbsolutePath: matchedSection.node.fileAbsolutePath,
          items: [],
        });

        // Run through articles and match up with actual data
        pocket.items.forEach(navItem => {
          let matchedArticle;

          // First attempt to find matching internal node
          matchedArticle = articles.find(
            ({ node }) =>
              node.frontmatter.title === navItem.title &&
              node.frontmatter.section === pocket.section
          );

          // Then try to look for URL field for external pages
          if (!matchedArticle && navItem.url) {
            // Stubbing out needed data structure from normal article so we can re-use the same logic
            matchedArticle = {
              node: {
                fileAbsolutePath: navItem.url,
                frontmatter: {
                  title: navItem.title,
                  section: pocket.section,
                },
                fields: {
                  fullUrl: navItem.url,
                },
              },
            };
          }

          if (matchedArticle) {
            menu[index].items.push({
              ...matchedArticle.node,
            });
          }
        });
      }
    });

    return menu;
  };

  const renderNav = () => (
    <ul className="docs__nav-list">
      <li>
        <Button
          onClick={() => {
            handleNavClick();
            navigate('/docs/');
          }}
          type="reset"
          className={cx('docs__nav-list-categoryTrigger', {
            [ckConfig.classes.active]: location.pathname === '/docs/',
          })}
        >
          <Icon
            className="docs__nav-list-categoryTrigger-icon"
            icon="docs__overview"
          />
          <span className="docs__nav-list-categoryTrigger-text">Overview</span>
        </Button>
      </li>
      {getMenuItems().map(menuSection => {
        // If the section returned doesn't have any items, don't show
        if (!menuSection.items.length) return;

        return (
          <li key={menuSection.fileAbsolutePath}>
            <Reveal
              reveal={article.frontmatter.section === menuSection.title} // Determine if we're "within" this section. If we are, reveal secondary nav by default
              trigger={{
                label: (
                  <Fragment>
                    <Icon
                      className="docs__nav-list-categoryTrigger-icon"
                      icon={menuSection.icon || 'docs__fallback'}
                      fallback="docs__fallback"
                    />
                    <span className="docs__nav-list-categoryTrigger-text">
                      {menuSection.title}
                    </span>
                  </Fragment>
                ),
                props: {
                  type: 'reset',
                  className: 'docs__nav-list-categoryTrigger',
                },
              }}
            >
              <ul className="docs__nav-list-sub">
                {menuSection.items.map(item => (
                  <li key={item.fileAbsolutePath}>
                    {isExternalUrl(item.fields.fullUrl) ? (
                      <a
                        className="u-link"
                        href={item.fields.fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.frontmatter.title}
                      </a>
                    ) : (
                      <Link
                        className="u-link"
                        to={item.fields.fullUrl}
                        onClick={() => handleNavClick()}
                      >
                        {item.frontmatter.title}
                      </Link>
                    )}
                    {item.fileAbsolutePath === article.fileAbsolutePath &&
                      article.tableOfContents &&
                      parser(article.tableOfContents)}
                  </li>
                ))}
              </ul>
            </Reveal>
          </li>
        );
      })}
    </ul>
  );

  // Find article in nav and create prev/next links
  const renderFooter = () => {
    // Get current section (and menu items)
    const currentSection = getMenuItems().find(
      menuSection => menuSection.title === article.frontmatter.section
    );

    // This is useful for the main "Overview" page which we won't appear in the menu array
    if (!currentSection) return false;

    // Find where this article appears in the list of items
    const articleNavigationMatchIndex = currentSection.items.findIndex(
      item => item.fileAbsolutePath === article.fileAbsolutePath
    );

    const previousArticle =
      currentSection.items[articleNavigationMatchIndex - 1];
    const nextArticle = currentSection.items[articleNavigationMatchIndex + 1];

    // If no navigation items are found, don't render footer
    if (!previousArticle && !nextArticle) return false;

    return (
      <div className="docs__footer">
        {previousArticle && (
          <Tooltip
            placement="right"
            content={previousArticle.frontmatter.title}
          >
            <Button
              className="docs__footer-previous"
              url={previousArticle.fields.fullUrl}
              size="small"
              type="default"
              target={
                isExternalUrl(previousArticle.fields.fullUrl) ? '_blank' : null
              }
              rel={
                isExternalUrl(previousArticle.fields.fullUrl)
                  ? 'noopener noreferrer'
                  : null
              }
            >
              Previous
            </Button>
          </Tooltip>
        )}
        {nextArticle && (
          <Tooltip placement="left" content={nextArticle.frontmatter.title}>
            <Button
              className="docs__footer-next"
              url={nextArticle.fields.fullUrl}
              size="small"
              type="default"
              target={
                isExternalUrl(nextArticle.fields.fullUrl) ? '_blank' : null
              }
              rel={
                isExternalUrl(nextArticle.fields.fullUrl)
                  ? 'noopener noreferrer'
                  : null
              }
            >
              Next
            </Button>
          </Tooltip>
        )}
      </div>
    );
  };

  // Get around duplicated section/article titles
  const title =
    article.frontmatter.section !== article.frontmatter.title
      ? `${article.frontmatter.section} > ${article.frontmatter.title}`
      : article.frontmatter.title;

  useLockBodyScroll(isMobileNavOpen);

  return (
    <Foundation>
      <Helmet
        title={title}
        titleTemplate="%s | Gremlin Docs"
        meta={[{ name: 'node-type', content: nodeType }]}
      />
      <div className="site-wrapper">
        <aside className="docs__nav u-contrast">
          <header className="docs__nav-header">
            <Link to="/docs/" className="docs__nav-logoLink">
              <img src={logoDocs} alt={`${config.siteMetadata.title} Docs`} />
            </Link>
          </header>
          <nav className="docs__nav-menu">{renderNav()}</nav>
          <div className="docs__nav-cta u-gradient--darkBlue-purple u-textCenter">
            <h3 className="u-textMedium u-textHeading">
              Avoid downtime. Use Gremlin to turn failure into resilience.
            </h3>
            <Button
              url="https://app.gremlin.com/signup"
              type="secondary"
              size="xsmall"
              target="_blank"
              rel="noopener noreferrer"
            >
              Use for Free
            </Button>
          </div>
        </aside>
        <main className="docs__content-wrapper">
          <header className="docs__header-wrapper">
            <div className="container container--left u-pl--remove">
              <div className="docs__header">
                <Link
                  to="/docs/"
                  className="u-inlineBlock u-hiddenUp@medium u-pl--regular"
                >
                  <img
                    style={{ height: styles.height.xsmall }}
                    src={logoMascot}
                    alt={`${config.siteMetadata.title} Docs`}
                  />
                </Link>
                <DocSearch />
                <Button
                  type="reset"
                  onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                  className="docs__header-mobileToggle u-hiddenUp@medium"
                >
                  <Icon size="large" icon="menu" />
                </Button>
                <Button
                  className="docs__header-cta u-hiddenDown@small"
                  size="xsmall"
                  type="default"
                  url="https://app.gremlin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Dashboard
                </Button>
              </div>
            </div>
          </header>
          <article className="docs__content">
            <div className="container container--left docs__content-container">
              <div className="u-mb--large">
                <div className="u-textMuted">{article.frontmatter.section}</div>
                <h1 className="u-textFluid--h2-h3 u-mt--remove u-mb--small">
                  {article.frontmatter.title}
                </h1>
              </div>
              {children}
              {renderFooter()}
            </div>
          </article>
        </main>
      </div>
    </Foundation>
  );
};

DocsLayout.propTypes = {
  children: PropTypes.node,
  article: PropTypes.object,
  nodeType: PropTypes.string,
  location: PropTypes.object.isRequired,
};

export default DocsLayout;
